import _ from 'lodash'
import { baseApi, Tag } from './base'
import {
  Planning,
  CreatePlanningParams,
  UpdatePlanningParams,
  GetPlanningsParams,
  PlanningOverviewDto,
  PlanningStatsDto,
  GetPlanningOverviewParams,
  EligibleStaffResponse,
  EligibleStaffParams,
  ShiftProposal,
  CreateShiftProposalParams,
  ReviewShiftProposalParams,
} from '../models/planning.model'
import { CreateShiftParams, CreateShiftTemplateParams, GetShiftsParams, Shift, ShiftTemplate, UpdateShiftTemplateParams } from '../models/shift.model'
import { cacher } from '@/utils/rtkQueryCacheUtils'

export type ShiftException = {
  _id: string
  planning: string
  shiftTemplate: string
  startTime: string
  endTime: string
}

export type CreateShiftExceptionParams = Omit<ShiftException, '_id' | 'planning'> & { planningId: string }
export type GetShiftExceptionsParams = { planningId: string; dateFrom?: string; dateTo?: string }

export const planningApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createPlanning: builder.mutation<Planning, CreatePlanningParams>({
      query: (body) => ({ url: '/plannings', method: 'POST', body }),
      invalidatesTags: cacher.invalidatesLists([Tag.PLANNING]),
    }),
    getPlannings: builder.query<Planning[], GetPlanningsParams>({
      query: (params) => ({ url: '/plannings', method: 'GET', params }),
      providesTags: cacher.providesList(Tag.PLANNING),
    }),
    getPlanning: builder.query<Planning, string>({
      query: (planningId) => ({ url: `/plannings/${planningId}`, method: 'GET' }),
      providesTags: cacher.cacheByIdArg(Tag.PLANNING),
    }),
    updatePlanning: builder.mutation<Planning, { planningId: string; updatePlanningParams: UpdatePlanningParams }>({
      query: ({ planningId, updatePlanningParams }) => ({ url: `/plannings/${planningId}`, method: 'PATCH', body: updatePlanningParams }),
      async onQueryStarted({ planningId, updatePlanningParams }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedPlanning } = await queryFulfilled
          const getPlanningsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlannings')[0]
          dispatch(
            planningApi.util.updateQueryData('getPlannings', getPlanningsArgs, (cachedPlannings) => {
              const index = cachedPlannings.findIndex((planning) => planning._id === planningId)
              if (index !== -1) {
                cachedPlannings[index] = updatedPlanning
              }
            })
          )
        } catch {}
      },
    }),
    archivePlanning: builder.mutation<Planning, string>({
      query: (planningId) => ({ url: `/plannings/${planningId}/archive`, method: 'PATCH' }),
      async onQueryStarted(planningId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedPlanning } = await queryFulfilled

          // Update the planning in the getPlannings cache
          const getPlanningsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlannings')[0]
          if (getPlanningsArgs) {
            dispatch(
              planningApi.util.updateQueryData('getPlannings', getPlanningsArgs, (cachedPlannings) => {
                const index = cachedPlannings.findIndex((planning) => planning._id === planningId)
                if (index !== -1) {
                  cachedPlannings[index] = updatedPlanning
                }
              })
            )
          }

          // Update the planning in the getPlanningOverview cache
          const getPlanningOverviewArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlanningOverview')[0]
          if (getPlanningOverviewArgs) {
            dispatch(
              planningApi.util.updateQueryData('getPlanningOverview', getPlanningOverviewArgs, (cachedPlannings) => {
                const index = cachedPlannings.findIndex((planning) => planning._id === planningId)
                if (index !== -1) {
                  cachedPlannings[index] = {
                    ...cachedPlannings[index],
                    archived: true,
                  }
                }
              })
            )
          }
        } catch {}
      },
    }),
    unarchivePlanning: builder.mutation<Planning, string>({
      query: (planningId) => ({ url: `/plannings/${planningId}/unarchive`, method: 'PATCH' }),
      async onQueryStarted(planningId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedPlanning } = await queryFulfilled

          // Update the planning in the getPlannings cache
          const getPlanningsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlannings')[0]
          if (getPlanningsArgs) {
            dispatch(
              planningApi.util.updateQueryData('getPlannings', getPlanningsArgs, (cachedPlannings) => {
                const index = cachedPlannings.findIndex((planning) => planning._id === planningId)
                if (index !== -1) {
                  cachedPlannings[index] = updatedPlanning
                }
              })
            )
          }

          // Update the planning in the getPlanningOverview cache
          const getPlanningOverviewArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlanningOverview')[0]
          if (getPlanningOverviewArgs) {
            dispatch(
              planningApi.util.updateQueryData('getPlanningOverview', getPlanningOverviewArgs, (cachedPlannings) => {
                const index = cachedPlannings.findIndex((planning) => planning._id === planningId)
                if (index !== -1) {
                  cachedPlannings[index] = {
                    ...cachedPlannings[index],
                    archived: false,
                  }
                }
              })
            )
          }
        } catch {}
      },
    }),
    deletePlanning: builder.mutation<string, string>({
      query: (planningId) => ({ url: `/plannings/${planningId}`, method: 'DELETE' }),
      async onQueryStarted(planningId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled
          const getPlanningsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getPlannings')[0]
          if (getPlanningsArgs) {
            dispatch(
              planningApi.util.updateQueryData('getPlannings', getPlanningsArgs, (cachedPlannings) => {
                return cachedPlannings.filter((planning) => planning._id !== planningId)
              })
            )
          }
        } catch {}
      },
    }),
    addScheduleToPlanning: builder.mutation<Planning, { planningId: string; scheduleId: string }>({
      query: ({ planningId, scheduleId }) => ({ url: `/plannings/${planningId}/schedules/${scheduleId}`, method: 'POST' }),
    }),
    removeScheduleFromPlanning: builder.mutation<Planning, { planningId: string; scheduleId: string }>({
      query: ({ planningId, scheduleId }) => ({ url: `/plannings/${planningId}/schedules/${scheduleId}`, method: 'DELETE' }),
    }),
    getCoordinationOverview: builder.query<any, { startDate: string; endDate: string }>({
      query: (params) => ({ url: '/plannings/coordination/overview', method: 'GET', params }),
    }),
    sendNotifications: builder.mutation<any, { planningId: string; notificationData: any }>({
      query: ({ planningId, notificationData }) => ({ url: `/plannings/${planningId}/notifications`, method: 'POST', body: notificationData }),
    }),
    getUtilizationReport: builder.query<any, { startDate: string; endDate: string }>({
      query: (params) => ({ url: '/plannings/reports/utilization', method: 'GET', params }),
    }),
    getAvailabilityTrends: builder.query<any, { startDate: string; endDate: string }>({
      query: (params) => ({ url: '/plannings/reports/availability-trends', method: 'GET', params }),
    }),
    createShift: builder.mutation<Shift, CreateShiftParams>({
      query: (body) => ({ url: `/plannings/${body.planningId}/shifts`, method: 'POST', body: _.omit(body, 'planningId') }),
      async onQueryStarted({ planningId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: createdShift } = await queryFulfilled
          dispatch(
            planningApi.util.updateQueryData('getPlanning', planningId, (cachedPlanning) => {
              cachedPlanning.shifts.push(createdShift)
            })
          )
        } catch {}
      },
    }),
    getShiftTemplates: builder.query<ShiftTemplate[], string>({
      query: (planningId) => ({ url: `/plannings/${planningId}/shift-templates`, method: 'GET' }),
      providesTags: cacher.providesList(Tag.SHIFT_TEMPLATE),
    }),

    createShiftTemplate: builder.mutation<ShiftTemplate, CreateShiftTemplateParams>({
      query: (body) => ({ url: `/plannings/${body.planning}/shift-templates`, method: 'POST', body }),
      async onQueryStarted({ planning }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: createdShiftTemplate } = await queryFulfilled
          dispatch(
            planningApi.util.updateQueryData('getShiftTemplates', planning, (cachedShiftTemplates) => {
              cachedShiftTemplates.push(createdShiftTemplate)
            })
          )
        } catch {}
      },
      invalidatesTags: (_result, _error, { planning }) => [{ type: Tag.PLANNING, id: planning }],
    }),

    updateShiftTemplate: builder.mutation<ShiftTemplate, { planningId: string; shiftTemplateId: string; updateShiftTemplateParams: UpdateShiftTemplateParams }>(
      {
        query: ({ planningId, shiftTemplateId, updateShiftTemplateParams }) => ({
          url: `/plannings/${planningId}/shift-templates/${shiftTemplateId}`,
          method: 'PATCH',
          body: updateShiftTemplateParams,
        }),
        async onQueryStarted({ planningId, shiftTemplateId }, { dispatch, getState, queryFulfilled }) {
          try {
            const { data: updatedShiftTemplate } = await queryFulfilled
            dispatch(
              planningApi.util.updateQueryData('getShiftTemplates', planningId, (cachedShiftTemplates) => {
                const index = cachedShiftTemplates.findIndex((template) => template._id === shiftTemplateId)
                if (index !== -1) {
                  cachedShiftTemplates[index] = updatedShiftTemplate
                }
              })
            )
          } catch {}
        },
      }
    ),

    deleteShiftTemplate: builder.mutation<ShiftTemplate, { planningId: string; shiftTemplateId: string }>({
      query: ({ planningId, shiftTemplateId }) => ({ url: `/plannings/${planningId}/shift-templates/${shiftTemplateId}`, method: 'DELETE' }),
      async onQueryStarted({ planningId, shiftTemplateId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            planningApi.util.updateQueryData('getShiftTemplates', planningId, (cachedShiftTemplates) => {
              return cachedShiftTemplates.filter((template) => template._id !== shiftTemplateId)
            })
          )
        } catch {}
      },
    }),

    getShifts: builder.query<Shift[], GetShiftsParams>({
      query: ({ planningId, ...params }) => ({ url: `/plannings/${planningId}/shifts`, method: 'GET', params }),
      providesTags: cacher.providesList(Tag.SHIFT),
    }),

    // New endpoints for individual shift operations
    getShift: builder.query<Shift, { planningId: string; shiftId: string }>({
      query: ({ planningId, shiftId }) => ({ url: `/plannings/${planningId}/shifts/${shiftId}`, method: 'GET' }),
    }),

    updateShift: builder.mutation<Shift, { planningId: string; shiftId: string; updateShiftParams: any }>({
      query: ({ planningId, shiftId, updateShiftParams }) => ({
        url: `/plannings/${planningId}/shifts/${shiftId}`,
        method: 'PATCH',
        body: updateShiftParams,
      }),
      async onQueryStarted({ planningId, shiftId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedShift } = await queryFulfilled

          // Update the shifts list if it exists in cache
          const getShiftsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getShifts')[0]
          if (getShiftsArgs) {
            dispatch(
              planningApi.util.updateQueryData('getShifts', getShiftsArgs, (cachedShifts) => {
                const index = cachedShifts.findIndex((shift) => shift._id === shiftId)
                if (index !== -1) {
                  cachedShifts[index] = updatedShift
                }
              })
            )
          }

          // Update the individual shift in cache if it exists
          dispatch(planningApi.util.updateQueryData('getShift', { planningId, shiftId }, () => updatedShift))
        } catch {}
      },
    }),

    deleteShift: builder.mutation<void, { planningId: string; shiftId: string }>({
      query: ({ planningId, shiftId }) => ({ url: `/plannings/${planningId}/shifts/${shiftId}`, method: 'DELETE' }),
      async onQueryStarted({ planningId, shiftId }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled

          // Remove from shifts list if it exists in cache
          const getShiftsArgs = planningApi.util.selectCachedArgsForQuery(getState(), 'getShifts')[0]
          if (getShiftsArgs) {
            dispatch(
              planningApi.util.updateQueryData('getShifts', getShiftsArgs, (cachedShifts) => {
                return cachedShifts.filter((shift) => shift._id !== shiftId)
              })
            )
          }

          // Update the planning if it exists in cache
          dispatch(
            planningApi.util.updateQueryData('getPlanning', planningId, (cachedPlanning) => {
              cachedPlanning.shifts = cachedPlanning.shifts.filter((shift) => shift._id !== shiftId)
            })
          )
        } catch {}
      },
    }),

    getPlanningOverview: builder.query<PlanningOverviewDto[], GetPlanningOverviewParams>({
      query: (params) => ({ url: '/plannings/overview', method: 'GET', params }),
      providesTags: cacher.providesList(Tag.PLANNING),
    }),

    getPlanningStats: builder.query<PlanningStatsDto, { networkId?: string; startDate?: string; endDate?: string }>({
      query: (params) => ({ url: '/plannings/stats', method: 'GET', params }),
    }),

    createShiftException: builder.mutation<ShiftException, CreateShiftExceptionParams>({
      query: ({ planningId, ...body }) => ({ url: `/plannings/${planningId}/shift-exceptions`, method: 'POST', body }),
      invalidatesTags: cacher.invalidatesLists([Tag.SHIFT, Tag.SHIFT_TEMPLATE, Tag.SHIFT_EXCEPTION]),
    }),

    getShiftExceptions: builder.query<ShiftException[], GetShiftExceptionsParams>({
      query: ({ planningId, ...params }) => ({ url: `/plannings/${planningId}/shift-exceptions`, method: 'GET', params }),
      providesTags: cacher.providesList(Tag.SHIFT_EXCEPTION),
    }),

    createShiftProposal: builder.mutation<ShiftProposal, CreateShiftProposalParams>({
      query: ({ planningId, ...body }) => ({ url: `/plannings/${planningId}/proposals`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { planningId }) => [
        { type: Tag.SHIFT_PROPOSAL, id: planningId },
        { type: Tag.SHIFT_PROPOSAL, id: 'mine-' + planningId },
      ],
    }),

    getPendingShiftProposals: builder.query<ShiftProposal[], string>({
      query: (planningId) => ({ url: `/plannings/${planningId}/proposals`, method: 'GET' }),
      providesTags: (_result, _error, planningId) => [{ type: Tag.SHIFT_PROPOSAL, id: planningId }],
    }),

    getMyShiftProposals: builder.query<ShiftProposal[], string>({
      query: (planningId) => ({ url: `/plannings/${planningId}/proposals/mine`, method: 'GET' }),
      providesTags: (_result, _error, planningId) => [{ type: Tag.SHIFT_PROPOSAL, id: 'mine-' + planningId }],
    }),

    approveShiftProposal: builder.mutation<ShiftProposal, ReviewShiftProposalParams>({
      query: ({ planningId, proposalId, reason }) => ({ url: `/plannings/${planningId}/proposals/${proposalId}/approve`, method: 'PATCH', body: { reason } }),
      invalidatesTags: (_result, _error, { planningId }) => [
        { type: Tag.SHIFT_PROPOSAL, id: planningId },
        { type: Tag.SHIFT_PROPOSAL, id: 'mine-' + planningId },
        { type: Tag.SHIFT, id: 'LIST' },
        { type: Tag.SHIFT_TEMPLATE, id: 'LIST' },
      ],
    }),

    rejectShiftProposal: builder.mutation<ShiftProposal, ReviewShiftProposalParams>({
      query: ({ planningId, proposalId, reason }) => ({ url: `/plannings/${planningId}/proposals/${proposalId}/reject`, method: 'PATCH', body: { reason } }),
      invalidatesTags: (_result, _error, { planningId }) => [
        { type: Tag.SHIFT_PROPOSAL, id: planningId },
        { type: Tag.SHIFT_PROPOSAL, id: 'mine-' + planningId },
      ],
    }),

    withdrawShiftProposal: builder.mutation<ShiftProposal, { planningId: string; proposalId: string }>({
      query: ({ planningId, proposalId }) => ({ url: `/plannings/${planningId}/proposals/${proposalId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { planningId }) => [
        { type: Tag.SHIFT_PROPOSAL, id: planningId },
        { type: Tag.SHIFT_PROPOSAL, id: 'mine-' + planningId },
      ],
    }),

    getEligibleStaffs: builder.query<EligibleStaffResponse[], EligibleStaffParams>({
      query: ({ planningId, ...params }) => ({
        url: `/plannings/${planningId}/eligible-staffs`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: Tag.STAFF, id: 'ELIGIBLE' }],
    }),
  }),
  overrideExisting: false,
})
